import { content, dayOf, message, spend, type LifeEvent, type LifeState } from './life.ts';
import { hereCountry, localPrice, nudge } from './character.ts';
import { policyShare } from './policy.ts';
import { ageOf } from './lifecycle.ts';
import { eduLabelFor } from './spawn.ts';
import { Rng } from './rng.ts';

/**
 * University, start to finish: get in (a visible checklist: your schooling, your grades, the fees, and no full-time
 * job), go to class, and graduate with a degree in your field after enough days of it. Medicine is the long one and
 * asks for the best grades; a medical degree then needs the licensing exam (careers: 'mdcn') before a hospital will
 * hire you. Lives born at university are already partway through, by their age.
 */

export interface Uni { field: string; place: string; done: number; need: number; started: number }

/** class days to a degree (game time: a degree is weeks of play, not years) */
const DAYS: Record<string, number> = { medicine: 20, law: 32 }; // (medicine was 45: too long a grind in play; the user's call, 2026-10-14)
const daysFor = (field: string) => DAYS[field] ?? 26;
/** the grade you need to get in (academics), by course */
const CUTOFF: Record<string, number> = { medicine: 50, law: 40, computing: 32, engineering: 32 };
export const cutoffFor = (field: string) => CUTOFF[field] ?? 25;
/** the whole programme's fees, in naira (made local where you are) */
const FEES: Record<string, number> = { medicine: 900_000, law: 600_000 };
export const feesFor = (s: LifeState, field: string) => localPrice(s, (FEES[field] ?? 450_000) * policyShare(hereCountry(s), 'school_fees'), 1000);

/** the universities that teach it (an art school doesn't teach medicine; a polytechnic, not medicine or law) */
function schoolsFor(s: LifeState, field: string): string[] {
  const all = content(s).institutions.student;
  const fits = all.filter((n) => !(/Beaux-Arts/.test(n) && field !== 'arts') && !(/Sciences Po/.test(n) && !['law', 'economics', 'business'].includes(field)) && !(/YabaTech|LASUSTECH|Normal/.test(n) && ['medicine', 'law'].includes(field)));
  return fits.length ? fits : all;
}
const fieldById = (s: LifeState, id: string) => content(s).fields.find((f) => f.id === id);
const fieldByLabel = (s: LifeState, label: string | null) => content(s).fields.find((f) => f.label === label);

/** Your course, if you're at university (made for lives born there: partway through, by age). */
export function uniOf(s: LifeState): Uni | null {
  if (s.card.education.level !== 'student') return null;
  if (!s.uni) {
    const f = fieldByLabel(s, s.card.education.field) ?? content(s).fields[0];
    const need = daysFor(f.id), years = Math.max(0, ageOf(s) - 18);
    s.uni = { field: f.id, place: s.card.education.place ?? content(s).institutions.student[0], done: Math.min(need - 6, Math.round((years / 5) * need)), need, started: s.minutes };
  }
  // a course made shorter since you enrolled: the new length, the same share of it done
  const need = daysFor(s.uni.field);
  if (s.uni.need > need) { s.uni.done = Math.floor((s.uni.done / s.uni.need) * need); s.uni.need = need; }
  return s.uni;
}

export interface AdmissionCheck { label: string; met: boolean; have: string; how: string }

/** What getting into a course needs, read from your life (refusals name what's missing). */
export function admission(s: LifeState, field: string): AdmissionCheck[] {
  const lvl = s.card.education.level, a = Math.round(s.skills.academics), cut = cutoffFor(field), fees = feesFor(s, field);
  return [
    { label: 'Finished secondary school', met: lvl !== 'secondary' || ageOf(s) >= 17, have: `you're ${ageOf(s)}`, how: '' },
    { label: 'Not already at university', met: lvl !== 'student', have: lvl === 'student' ? 'you are' : 'no', how: 'finish your course first' },
    { label: 'Not in a full-time job', met: !s.job, have: s.job ? `you work as ${s.job.label.toLowerCase()}` : 'no job', how: 'leave your job (Jobs)' },
    { label: `Academics ${cut}+ (the entry grade)`, met: a >= cut, have: `you're at ${a}`, how: 'study: courses, books, classes' },
    { label: `Fees: ${fees.toLocaleString('en-GB')}`, met: s.money >= fees, have: `you have ${Math.round(s.money).toLocaleString('en-GB')}`, how: 'save up' },
  ];
}

/** The courses you could apply for here (every field), with what each needs. */
export function courses(s: LifeState) {
  return content(s).fields.map((f) => ({ id: f.id, label: f.label, days: daysFor(f.id), fees: feesFor(s, f.id), checks: admission(s, f.id) }));
}

/** Apply and pay: you're a student from tomorrow (classes on the student timetable). */
export function enrolUni(s: LifeState, field: string): string | null {
  const f = fieldById(s, field);
  if (!f) return 'No such course.';
  const miss = admission(s, field).filter((c) => !c.met);
  if (miss.length) return `Not yet: ${miss.map((c) => c.label.toLowerCase()).join(', ')}.`;
  if (!spend(s, feesFor(s, field), `University fees: ${f.label}`)) return "You can't afford the fees.";
  const r = new Rng((s.seed ^ Math.imul(dayOf(s.minutes) + 7, 0x9e3779b1)) >>> 0);
  const place = r.pick(schoolsFor(s, field));
  s.card.education = { level: 'student', label: eduLabelFor(content(s).id, 'student', f.label), field: f.label, place };
  s.uni = { field: f.id, place, done: 0, need: daysFor(f.id), started: s.minutes };
  s.history.push({ t: s.minutes, text: `Started ${f.label} at ${place}.` });
  nudge(s, 'ambitious', 0.03);
  return null;
}

/** A day of classes done (from the shift): a step towards the degree; the last one graduates you. */
export function classDay(s: LifeState, performance: number, events: LifeEvent[]): string {
  const u = uniOf(s);
  if (!u) return '';
  u.done = Math.min(u.need, u.done + (performance >= 0.4 ? 1 : 0.5));
  if (u.done < u.need) return ` ${Math.floor(u.need - u.done)} class days to your degree.`;
  const f = fieldById(s, u.field)!;
  s.card.education = { level: 'degree', label: eduLabelFor(content(s).id, 'degree', f.label), field: f.label, place: u.place };
  s.uni = null;
  s.history.push({ t: s.minutes, text: `Graduated: ${s.card.education.label}, ${u.place}.` });
  nudge(s, 'ambitious', 0.04);
  const parent = s.contacts.find((p) => p.relation === 'Mother') ?? s.contacts.find((p) => p.relation === 'Father');
  if (parent) message(s, parent.name, u.field === 'medicine' ? 'A doctor in the family! I told everyone at church. Now the licensing exam, then a hospital.' : `You graduated! We are so proud of you.`, events);
  events.push({ t: s.minutes, kind: 'info', text: `You graduated: ${s.card.education.label}.${u.field === 'medicine' ? ' Next: the medical licensing exam (Jobs → Courses), then apply to hospitals.' : ' Jobs that ask for a degree are open to you now.'}` });
  return ` That was your last class day: you've graduated.`;
}
